import styled from '@emotion/styled';
import { Input } from 'antd';
import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { AppState } from 'src/config/appState';
import { StepButtons, StepButtonsProps } from 'src/modules/programs/components/StepButtons';
import { Topics } from 'src/modules/programs/components/Topics';
import * as S from 'src/modules/programs/stepComponents/styles';
import { CircularRates } from 'src/shared/components/CircularRates';
import { color, font } from 'src/shared/styles/variables';

import {
    changeValuesAssessmentAreaField,
    submitValuesAssessmentsData,
} from '../actions/weekTwoActions';
import { ValuesAssessmentDomain } from '../models/weekTwo';
import { valuesAssessmentDomains } from '../models/weekTwoData';
import { getValuesAssessments, isValuesAssessmentsDataValid } from '../selectors/weekTwoSelectors';

const MainQuestion = styled('h3')(
    {
        color: color.chathamsBlue,
        ':not(:first-child)': {
            marginTop: '3rem',
        },
    },
    font.heavy(font.size.mediumExtended),
);

const QuestionsListHeader = styled('h4')(
    {
        color: color.tundora,
        margin: '0 0 1rem',
    },
    font.medium(font.size.medium),
);

const QuestionsList = styled('ol')(
    {
        color: color.tundora,
        padding: 0,
    },
    font.book(font.size.medium),
);

const Question = styled('li')({
    display: 'block',
});

const { TextArea } = Input;

const StyledTextArea = styled(TextArea)({
    marginBottom: '2rem',
});

interface ValueTabContentProps {
    domainTitle: string;
    questions: string[];
    topics: string[];
    valuesAssessments: ValuesAssessmentDomain[];
    placeholder?: string;
    onTopicChange: (domainTitle: string, value: string) => void;
    onAdditionalInfoChange: (domainTitle: string, value: string) => void;
    onRateChange: (domainTitle: string, value: number) => void;
}

const ValuesAssessmentDomainTab: React.SFC<ValueTabContentProps> = ({
    domainTitle,
    questions,
    topics,
    placeholder,
    onTopicChange,
    onAdditionalInfoChange,
    onRateChange,
    valuesAssessments,
}) => {
    const areaInput = valuesAssessments.find(
        assessmentDomain => assessmentDomain.title === domainTitle,
    );

    return areaInput ? (
        <>
            <MainQuestion>What are your {domainTitle} values?</MainQuestion>
            <QuestionsListHeader>Questions to get you thinking</QuestionsListHeader>
            <QuestionsList>
                {questions.map(question => (
                    <Question key={question}>{question}</Question>
                ))}
            </QuestionsList>
            <StyledTextArea
                autosize={{ minRows: 2 }}
                value={areaInput.additionalInfo}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    onAdditionalInfoChange(domainTitle, e.target.value)
                }
                placeholder={
                    placeholder ||
                    'Please write your own values here (a word or a sentence), or pick some from below.'
                }
            />
            <Topics
                capitalize
                topics={topics}
                selectedTopics={areaInput.topics}
                onTopicChange={value => onTopicChange(domainTitle, value)}
            />
            <MainQuestion>How do you think you are doing in this area?</MainQuestion>
            <CircularRates
                range={5}
                currentRate={areaInput.rate}
                minLabel={`Not living according to my ${domainTitle} values`}
                maxLabel={`Living according to my ${domainTitle} values`}
                onRateChange={value => onRateChange(domainTitle, value)}
            />
        </>
    ) : null;
};

interface StateProps {
    valuesAssessments: ValuesAssessmentDomain[];
    isDataValid: boolean;
}

interface ActionsProps {
    changeValuesAssessmentAreaField: typeof changeValuesAssessmentAreaField;
    submitValuesAssessmentsData: typeof submitValuesAssessmentsData;
}

type Props = StepButtonsProps & StateProps & ActionsProps & WithTranslation;

export class ValuesAssessmentStepContainer extends React.Component<Props> {
    private handleAreaTopicChange = (domainTitle: string, value: string) =>
        this.props.changeValuesAssessmentAreaField({ domainTitle, value, field: 'topic' });

    private handleAreaAdditionalInfoChange = (domainTitle: string, value: string) =>
        this.props.changeValuesAssessmentAreaField({ domainTitle, value, field: 'additionalInfo' });

    public handleAreaRateChange = (domainTitle: string, value: number) => {
        this.props.changeValuesAssessmentAreaField({ domainTitle, value, field: 'rate' });
    };

    private handleSubmit = () => {
        const { isDataValid, valuesAssessments } = this.props;

        if (!isDataValid) {
            return;
        }

        this.props.submitValuesAssessmentsData(valuesAssessments);
        this.props.goToNextStep();
    };

    public render(): JSX.Element {
        return (
            <>
                <S.ContentRow>
                    <S.ContentColumn>
                        <S.StepHeading small>Values</S.StepHeading>
                        <S.StepHeading>What are your values?</S.StepHeading>
                        <S.StepDescription small>
                            Click through to identify your values in each of these areas.
                        </S.StepDescription>
                    </S.ContentColumn>
                </S.ContentRow>
                <S.StyledTabs defaultActiveKey="Relationships">
                    {valuesAssessmentDomains.map((domain, index) => (
                        <S.TabPane tab={`${index + 1}. ${domain.title}`} key={domain.title}>
                            <S.TabPaneContent>
                                <ValuesAssessmentDomainTab
                                    valuesAssessments={this.props.valuesAssessments}
                                    domainTitle={domain.title}
                                    questions={domain.questions}
                                    topics={domain.topics}
                                    placeholder={domain.placeholder}
                                    onTopicChange={this.handleAreaTopicChange}
                                    onRateChange={this.handleAreaRateChange}
                                    onAdditionalInfoChange={this.handleAreaAdditionalInfoChange}
                                />
                            </S.TabPaneContent>
                        </S.TabPane>
                    ))}
                </S.StyledTabs>
                <StepButtons
                    onPreviousClick={this.props.goToPreviousStep}
                    onNextClick={this.handleSubmit}
                    nextDisabled={!this.props.isDataValid}
                    nextDisabledTooltip={this.props.t(
                        'week-2.values-assessments.nextDisabledTooltip',
                    )}
                    hasNextStep
                />
            </>
        );
    }
}

const mapStateToProps = (state: AppState): StateProps => ({
    valuesAssessments: getValuesAssessments(state),
    isDataValid: isValuesAssessmentsDataValid(state),
});

export const ValuesAssessmentStep = withTranslation('cp')(
    connect(mapStateToProps, {
        changeValuesAssessmentAreaField,
        submitValuesAssessmentsData,
    })(ValuesAssessmentStepContainer),
);
